// Package imports
import { useEffect, useState, useRef, useContext } from "react";
import { Route, Routes, useParams, useNavigate, Outlet, Navigate } from "react-router-dom";

// Modals and Drawers
import AttachFileModal from "./overlay/attachFileModal";
import AuditLogDrawer from "./overlay/auditLogDrawer";
import ChatHistoryDrawer from "./overlay/chatHistoryDrawer";
import ChatSettingsDrawer from "./overlay/chatSettingsDrawer";
import DataSourceDrawer from "./overlay/dataSourceDrawer";
import DeleteChatModal from "./overlay/deleteChatModal";
import EulaModal from "./overlay/eulaModal";
import ExecuteTemplateActivityTracker from "./chatTemplates/overlay/executeTemplateActivityTracker";
import ExportChatModal from "./overlay/exportChatModal";
import ExportChatTracker from "./overlay/exportChatTracker";
import PlagiarismDisclaimerModal from "./overlay/plagiarismDisclaimerModal";
import PlagiarismSnackbar from "./components/PlagiarismSnackbar";
import RenameChatModal from "./overlay/renameChatModal";
import ShareChatDrawer from "./overlay/shareChatDrawer";
import UploadActivityTracker from "./overlay/uploadActivityTracker"
import DnDFileUpload from "../fusion/FileUpload/DragAndDrop";
// Pages
import SymphonyDataStores from "./dataStores/symphonyDataStores";
import SymphonyDataStoreView from "./dataStores/symphonyDataStoreView";
import TemplateForm from "./chatTemplates/symphonyTemplateForm";
import SymphonyTemplates from "./chatTemplates/symphonyTemplates";

// Misc
import UnlicensedModal from "./overlay/unlicensedModal";
import { useCompliance } from "./contexts/ComplianceContext";
import { useLicensing } from "./contexts/LicenseContext";
import { useResource } from "./contexts/ResourceContext";
import { useChat } from "./contexts/ChatContext";
import ProviderFactory, { symphonyAppProviders } from "../../utilities/ProviderFactory";
import { useUserProfile } from "./contexts/UserProfileContext";
import { useNewTemplate } from "./contexts/NewTemplateContext";
import LeaveSharedChatModal from "./overlay/leaveChatModal";
import { rejectToast } from "./utilities/UploadFeedback";
import { ToastContext } from "cai-fusion";
import AnalyzeRFP from "./templates/analyzeRFP";
import SymphonyChatPage from "./chat/symphonyChatPage";
import SymphonyChatTemplatePage from "./chat/symphonyChatTemplatePage";
import SymphonyNewChatPage from "./chat/symphonyNewChatPage";
import env from "../../configs/envConfig";
import { debugLog } from "../../configs/loggingConfig";

const SymphonyApp = () => {

    const { agreedToEULA, agreeToEULA, setAgreedToEULA, outstandingEULA } = useCompliance();
    const { isLicensedEnv, hasLicense } = useLicensing();
    const { createToast } = useContext(ToastContext);
    const loadingUserProfile = useUserProfile();

    const SymphonyAppPage = ({shouldRedirect = false}) => {
        
        const uploaderRef = useRef(null);

        // These are used for the file upload activity tracker.
        const { isExporting, processUrlParameters } = useChat();
        const { setChatTemplateId } = useNewTemplate();
        const { uploadingFilesCount, isUploading, handleFilesUpload } = useResource();

        const params = useParams();
        const navigate = useNavigate();

        // This code is only necessary to run when the page is loading for the first time.
		// Why are we doing it here? Because this is in the scope of the router component, so it can see the field as 'ChatId'.
        useEffect(() => {
            debugLog.log("[PARAMS]", params)
            if (shouldRedirect && params.ChatId) {
                navigate(`/${env.REACT_APP_BRANDING_APP_PATH ?? "maestro"}/chat/${params.ChatId}`);
            }

            processUrlParameters(params.ChatId);
            setChatTemplateId(params.TemplateId ?? undefined);
        }, [])

        return (
            <>
                <div className="o-page-section o-page-section l-pt-none">
                    <div className="o-page-section__content-container o-page-section__content-container--med-wide-fixed-width l-pt-none l-mt-neg-sm">
                        <div className="l-row">
                            <ChatHistoryDrawer />
                            {/* <div className="col-md-9 l-pb-xl">  */}
                            <div ref={uploaderRef} className="col-md-9">
                                <Outlet />
                                <DnDFileUpload
                                    onFileUpload={handleFilesUpload}
                                    targetElementRef={uploaderRef}
                                    showUploader={false}
                                    onRejectedFiles={(files) => {
                                        rejectToast(files, createToast);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {env.REACT_APP_ALLEGRO_HIDE_CHAT_SETTINGS !== "true" &&
                    <ChatSettingsDrawer />
                }

                {env.REACT_APP_ALLEGRO_HIDE_DATA_SOURCES !== "true" &&
                    <DataSourceDrawer />
                }
                
                <ShareChatDrawer />
                
                {env.REACT_APP_ALLEGRO_HIDE_ADD_FILES !== "true" && 
                    <AttachFileModal />
                }
                
                <AuditLogDrawer />
                
                <DeleteChatModal />

                <LeaveSharedChatModal />
                
                <RenameChatModal />
                
                <PlagiarismSnackbar />

                <PlagiarismDisclaimerModal />
                
                <ExportChatModal />
                
                <ExecuteTemplateActivityTracker />

                <UploadActivityTracker 
                    isHidden={!isUploading}
                    numFiles={uploadingFilesCount}
                />
                
                <ExportChatTracker
                    isHidden={!isExporting}
                />
                
                {outstandingEULA &&
                    <EulaModal
                        content={outstandingEULA.text}
                        onAccept={agreeToEULA}
                        onDecline={() => setAgreedToEULA(false)}
                    />
                }
                {(isLicensedEnv && !hasLicense && !loadingUserProfile) &&
                    <UnlicensedModal />
                }
            </>
        )
    }

    const ChatContextElements = agreedToEULA && (

        <ProviderFactory providers={symphonyAppProviders}>
            <Routes>
                {/* All Chat Pages */}
                <Route path="chat" element={<SymphonyAppPage />}>
                    <Route index element={<SymphonyNewChatPage />} />
                    <Route path=":ChatId?/*" element={<SymphonyChatPage />} />
                    <Route path="templates">
                        <Route path="rfp" element={<AnalyzeRFP />} />
                        <Route path=":TemplateId?/*" element={<SymphonyChatTemplatePage />} />
                    </Route>
                </Route>
                {/* All Data Stores Pages */}
                <Route path="data">
                    <Route index element={<SymphonyDataStores />} />
                    <Route path=":DataStoreId?/*" element={<SymphonyDataStoreView />} />
                </Route>
                {/* All Chat Templates Pages */}
                <Route path="templates">
                    <Route index element={<SymphonyTemplates />} />
                    <Route path="new" element={<TemplateForm isEditing={false} />} />
                    <Route path=":TemplateId?/*" element={<TemplateForm isEditing={true} />} />
                </Route>
                {/* Legacy / Parity Paths */}
                <Route path=":ChatId?/*" element={<SymphonyAppPage shouldRedirect />} />
                <Route index element={<Navigate to="chat" replace />} />
            </Routes>
        </ProviderFactory>
    );

    return ChatContextElements

};

export default SymphonyApp;